/**
 * CLI Debug Session Generator
 *
 * Creates a debug login token for browser-based debugging with Playwright.
 * Requires superadmin authentication.
 *
 * Usage:
 *   canary debug-session [--env dev|prod|local] [--api-url <url>]
 *
 * Outputs the login URL that can be used with Playwright to authenticate
 * as the debug agent user for read-only browser access.
 */

import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import process from "node:process";
import { resolveConfig, hasFlag } from "./auth.js";
import { fetchOrExit } from "./api-fetch.js";

type DebugSessionResponse = {
  ok: boolean;
  loginUrl?: string;
  expiresAt?: string;
  error?: string;
  message?: string;
};

async function writeDebugSession(loginUrl: string, expiresAt: string, apiUrl: string) {
  const dir = path.join(os.homedir(), ".config", "canary-cli");
  const filePath = path.join(dir, "debug-session.json");
  await fs.mkdir(dir, { recursive: true, mode: 0o700 });
  await fs.writeFile(
    filePath,
    JSON.stringify({ loginUrl, expiresAt, apiUrl, createdAt: new Date().toISOString() }, null, 2),
    { encoding: "utf8", mode: 0o600 }
  );
  return filePath;
}

export async function runDebugSession(argv: string[]): Promise<void> {
  const { apiUrl, token } = await resolveConfig(argv);
  const jsonOutput = hasFlag(argv, "--json");

  try {
    const res = await fetchOrExit(`${apiUrl}/auth/debug-session/create`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      token,
    });

    // Handle HTTP errors before parsing JSON
    if (!res.ok) {
      const text = await res.text();

      if (res.status === 403) {
        console.error("Error: Forbidden. Debug session creation requires superadmin access.");
        process.exit(1);
      }

      if (res.status === 404) {
        console.error(
          "Error: Endpoint not found. The debug-session feature may not be deployed to this environment."
        );
        process.exit(1);
      }

      // Try to parse error as JSON, fallback to raw text
      try {
        const errorJson = JSON.parse(text) as { error?: string; message?: string };
        console.error(`Error: ${errorJson.message ?? errorJson.error ?? text}`);
      } catch {
        console.error(`Error (${res.status}): ${text || res.statusText}`);
      }
      process.exit(1);
    }

    const json = (await res.json()) as DebugSessionResponse;

    if (!json.ok || !json.loginUrl) {
      console.error(`Error: ${json.message ?? json.error ?? "Failed to create debug session"}`);
      process.exit(1);
    }

    // Save session info
    const filePath = await writeDebugSession(json.loginUrl, json.expiresAt ?? "", apiUrl);

    if (jsonOutput) {
      console.log(
        JSON.stringify(
          {
            loginUrl: json.loginUrl,
            expiresAt: json.expiresAt,
            sessionFile: filePath,
          },
          null,
          2
        )
      );
    } else {
      console.log("Debug session created successfully.");
      console.log("");
      console.log(`Login URL: ${json.loginUrl}`);
      console.log(`Expires: ${json.expiresAt}`);
      console.log(`Session saved to: ${filePath}`);
      console.log("");
      console.log("Use this URL with Playwright to authenticate as the debug agent.");
      console.log("The token is single-use and expires in 5 minutes.");
    }
  } catch (err) {
    console.error(`Failed to create debug session: ${err}`);
    process.exit(1);
  }
}
